import React, { createContext, useContext, useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useParams } from '@fs/zion-router'
import { usePageWidth, useZoomPan } from '@fs/zion-ui'

import useCachedBackgroundImage from '../useCachedBackgroundImage/useCachedBackgroundImage'

const ImageSettingsContext = createContext()

export function ImageSettingsProvider({ children, initialBackgroundOpacity }) {
  const [cachedBackgroundImage, setCachedBackgroundImage] = useCachedBackgroundImage()
  const atWidth = usePageWidth()
  const { design, style } = useParams()
  const isArtisticDesign = style === 'artistic'
  const [firstDesign, setFirstDesign] = useState(design)
  const [backgroundImage, setBackgroundImage] = useState()
  const [imageHeight, setImageHeight] = useState(1240)
  const [imageWidth, setImageWidth] = useState(isArtisticDesign ? 1240 : 1880)
  const [initialZoom, setInitialZoom] = useState(0.6)
  const [hasDarkBackground, setHasDarkBackground] = useState(false)
  const [backgroundOpacity, setBackgroundOpacity] = useState(initialBackgroundOpacity ?? 100)
  const [zoomPanRefs, zoomPan] = useZoomPan()

  const initialPanX = imageWidth / 2
  const zoomPanProps = atWidth({
    default: { initialPanX, initialZoom: initialZoom / 2, viewOrigin: 'center' },
    lg: { initialPanX, initialZoom, viewOrigin: 'center' },
  })

  const updateCachedBackgroundImage = useCallback(
    (imageData) => {
      setCachedBackgroundImage(imageData)
      setBackgroundImage(imageData)
    },
    [setCachedBackgroundImage, setBackgroundImage]
  )

  const value = useMemo(
    () => ({
      backgroundImage,
      backgroundOpacity,
      cachedBackgroundImage,
      firstDesign,
      hasDarkBackground,
      IMAGE_HEIGHT: imageHeight,
      IMAGE_WIDTH: imageWidth,
      setBackgroundImage,
      setBackgroundOpacity,
      setCachedBackgroundImage,
      setFirstDesign,
      setHasDarkBackground,
      setImageHeight,
      setImageWidth,
      setInitialZoom,
      updateCachedBackgroundImage,
      zoomPan,
      zoomPanProps,
      zoomPanRefs,
    }),
    [
      backgroundImage,
      backgroundOpacity,
      cachedBackgroundImage,
      firstDesign,
      hasDarkBackground,
      imageHeight,
      imageWidth,
      setBackgroundImage,
      setBackgroundOpacity,
      setCachedBackgroundImage,
      setFirstDesign,
      setHasDarkBackground,
      setImageHeight,
      setImageWidth,
      setInitialZoom,
      updateCachedBackgroundImage,
      zoomPan,
      zoomPanProps,
      zoomPanRefs,
    ]
  )

  return <ImageSettingsContext.Provider value={value}>{children}</ImageSettingsContext.Provider>
}

ImageSettingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialBackgroundOpacity: PropTypes.number,
}

export default function useImageSettings() {
  return useContext(ImageSettingsContext)
}
